import React, { useMemo } from 'react';
import { Calendar, User, Loader2 } from 'lucide-react';
import { Button } from '../common/Button';
import { useGetUserProfilesQuery } from '../../store/slices/apiSlice';
import { buildDeliveryEngineerFilterOptions } from '../../utils/palletJobFields';

interface ManageRoutesFiltersProps {
  selectedDate: string;
  selectedEngineerId: string;
  onDateChange: (date: string) => void;
  onEngineerChange: (engineerId: string) => void;
  onLoadRoutes: () => void;
  isLoading?: boolean;
}

export const ManageRoutesFilters: React.FC<ManageRoutesFiltersProps> = ({
  selectedDate,
  selectedEngineerId,
  onDateChange,
  onEngineerChange,
  onLoadRoutes,
  isLoading = false,
}) => {
  const { data: userProfiles, isLoading: usersLoading } = useGetUserProfilesQuery({
    page: 1,
    page_size: 100,
    ordering: 'user__username',
  });

  const engineerOptions = useMemo(
    () => buildDeliveryEngineerFilterOptions(userProfiles),
    [userProfiles]
  );

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[200px]">
          <label htmlFor="delivery-date" className="block text-sm font-medium text-gray-700 mb-1">
            <Calendar className="w-4 h-4 inline mr-1" />
            Delivery Date
          </label>
          <input
            type="date"
            id="delivery-date"
            value={selectedDate}
            onChange={(e) => onDateChange(e.target.value)}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        <div className="flex-1 min-w-[250px]">
          <label htmlFor="delivery-engineer" className="block text-sm font-medium text-gray-700 mb-1">
            <User className="w-4 h-4 inline mr-1" />
            Engineer
          </label>
          {usersLoading ? (
            <div className="text-sm text-gray-500 py-2">Loading users...</div>
          ) : (
            <select
              id="delivery-engineer"
              value={selectedEngineerId}
              onChange={(e) => onEngineerChange(e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value="">Select Engineer...</option>
              {engineerOptions.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.full_name} • [{user.role}]
                </option>
              ))}
            </select>
          )}
        </div>

        <div>
          <Button
            onClick={onLoadRoutes}
            disabled={!selectedDate || !selectedEngineerId || isLoading}
            icon={isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : undefined}
          >
            {isLoading ? 'Loading...' : 'Load Routes'}
          </Button>
        </div>
      </div>

      {!usersLoading && engineerOptions.length === 0 && (
        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
          <p className="text-sm text-amber-800">
            No users with Delivery or Collection role found. Assign one of those roles to enable
            engineer filtering.
          </p>
        </div>
      )}
    </div>
  );
};
